
export const REGIONAL_IDENTITIES = [
    {
        id: 'east_coast',
        name: 'East Coast',
        description: 'Maritime architecture of the Gulf coast, characterized by horizontal massing, flat roofs, badgirs, and specific coral/wood materials.',
        guideline_data: {
            voice_instruction: "The East Coast is horizontal. Buildings must be WIDER than they are tall. Flat roofs ONLY. No pitched roofs. Use parapets to hide roof clutter.",
            forbidden_elements: [
                "pitched roof", "gabled roof", "triangular crenellations", "vertical tower massing",
                "reflective blue glass", "gold glass", "exposed roller shutters", "hvac units", "plastic arches",
                "red clay tiles", "mountain stone", "alpine style"
            ],
            default_style: 'Traditional',
            styles: {
                Traditional: {
                    massing: "Horizontal, rectaliner massing with deep shadows and solid coral-stone walls.",
                    roof_type: "Flat with crenelated Merkab-style parapets",
                    window_ratio: "25% (Small, recessed)",
                    materials: ["Coral Stone (Rough)", "Gypsum Plaster (Matte)", "Tamarisk Wood (Dark)"],
                    details: ["Round or Trefoil Arches", "Wood-carved doors", "Badgir wind towers"]
                },
                Transitional: {
                    massing: "Clean horizontal lines, simplified porous screens, lighter massing.",
                    roof_type: "Flat with simple horizontal bands",
                    window_ratio: "40% (Vertical openings)",
                    materials: ["Smooth Plaster (Earth tones)", "Teak Wood accents", "Matte Metal"],
                    details: ["Simplified Semicircular Arches", "Geometric wooden screens", "Deep shaded balconies"]
                },
                Contemporary: {
                    massing: "Abstracted horizontal volumes, floating planes, sharp edges.",
                    roof_type: "Flat with stepped terraces and clean lines",
                    window_ratio: "60% (Floor-to-ceiling, deeply recessed)",
                    materials: ["White Concrete", "High-performance Clear Glass", "Slatted Wood Screens", "Copper/Bronze Metal"],
                    details: ["Rectangular frames", "No Arches", "Dynamic shading louvers"]
                }
            },
            palette_instruction: "Use strictly East Coast Palette: Primary [RAL 9010 Pure White, RAL 9001 Cream, RAL 1019 Grey Beige]. Accents [RAL 6034 Pastel Turquoise, RAL 5000 Violet Blue].",
            pattern_images: [
                "/patterns/east_coast/ec_pattern_03.png",
                "/patterns/east_coast/ec_pattern_02.png",
                "/patterns/east_coast/ec_pattern_01.png"
            ],
            elements: [
                {
                    name: "Badgir",
                    type: "ventilation",
                    description: "Wind tower for natural ventilation.",
                    prompt_terms: ["badgir wind tower", "traditional wind catcher"]
                },
                {
                    name: "Roshan (East Coast Variation)",
                    type: "window",
                    description: "Simpler projecting wooden window than Hejazi style.",
                    prompt_terms: ["simple wooden projecting window", "teak wood shutter"]
                },
                {
                    name: "Trefoil Arch",
                    type: "arch",
                    description: "Three-lobed arch typical of the Gulf.",
                    prompt_terms: ["trefoil arch", "gulf style arch"]
                }
            ]
        }
    },
    {
        id: 'central_najdi',
        name: 'Central Najdi',
        description: 'Architectural style of the central Arabian plateau, characterized by mud-brick construction, triangular crenellations, and introverted courtyards.',
        guideline_data: {
            voice_instruction: "Central Najdi is earth-born. Use Mud/Adobe texture. Walls must be battered (sloping/tapering upwards). Triangle crenellations are mandatory on roofs. No large glass openings.",
            forbidden_elements: [
                "large glass curtain walls", "steel cladding", "pitched tiled roof", "blue/green glass",
                "white marble", "exposed concrete", "industrial aesthetic", "gothic arches"
            ],
            default_style: 'Traditional',
            styles: {
                Traditional: {
                    massing: "Heavy, fortress-like, tapering walls (battered), minimal openings.",
                    roof_type: "Triangular Crenellations (Shurrafat)",
                    window_ratio: "10% (Small triangular or vertical slits)",
                    materials: ["Mud Brick (Adobe)", "Straw-reinforced Mud", "Ethel Wood"],
                    details: ["Triangular crenellations", "Keel Arches", "Decorated wooden doors"]
                },
                Transitional: {
                    massing: "Rectilinear but retaining heavy mass, simplified details.",
                    roof_type: "Simplified Triangular Crenellations",
                    window_ratio: "25% (Vertical slots)",
                    materials: ["Textured Earth-toned Plaster", "Saudi Limestone accents", "Dark Wood"],
                    details: ["Geometric gypsum bands", "Screened windows", "Recessed entries"]
                },
                Contemporary: {
                    massing: "Minimalist cubes, earth tones, play of light and shadow.",
                    roof_type: "Clean parapet with subtle triangular motifs",
                    window_ratio: "45% (Deeply recessed, shaded)",
                    materials: ["Rammed Earth", "Textured Concrete (Beige)", "Corten Steel details", "Low-iron plain glass"],
                    details: ["Fractal triangle patterns", "Perforated metal screens", "Hidden lighting"]
                }
            },
            palette_instruction: "Strictly Earth Tones. Primary: [RAL 1001 Beige, RAL 1011 Brown Beige, RAL 8004 Copper Brown]. Accents: [RAL 9010 Pure White (Gypsum details only)].",
            pattern_images: [
                "/patterns/central_najdi/cn_pattern_01.png",
                "/patterns/central_najdi/cn_pattern_02.png",
                "/patterns/central_najdi/cn_pattern_03.png"
            ],
            elements: [
                {
                    name: "Shurrafat",
                    type: "parapet",
                    description: "Triangular crenellations along the roofline.",
                    prompt_terms: ["triangular crenellations", "shurrafat parapet", "sawtooth roof edge"]
                },
                {
                    name: "Triangular Windows",
                    type: "window",
                    description: "Small triangular openings for ventilation.",
                    prompt_terms: ["small triangular windows", "geometric ventilation holes"]
                },
                {
                    name: "Keel Arch",
                    type: "arch",
                    description: "Pointed arch typical of Najdi architecture.",
                    prompt_terms: ["keel arch", "pointed najdi arch"]
                }
            ]
        }
    },
    {
        id: 'northern_najdi',
        name: 'Northern Najdi',
        description: 'Architecture of the northern desert plateau, characterized by fortress-like massing, stone bases, and triangular geometric patterns.',
        guideline_data: {
            voice_instruction: "Northern Najdi is a fortress. It is arid and red. The base MUST be distinct stone. Walls are vertical. No white boxes. Use stepped merlons on the roof.",
            forbidden_elements: [
                "white stucco", "mediterranean style", "flat featureless parapet", "random window placement",
                "shiny metal cladding", "pasted decoration", "blue glass", "pitched roof"
            ],
            default_style: 'Traditional',
            styles: {
                Traditional: {
                    massing: "Vertical fortress-like rhythm, heavy stone base, tapering walls.",
                    roof_type: "Stepped Merlons (Crenelated)",
                    window_ratio: "15% (Small, alignments)",
                    materials: ["Stacked Red Sandstone", "Mud/Clay Upper", "Tamarisk Wood"],
                    details: ["Triangular Friezes", "Turmah (Lookouts)", "No Arches (Lintels only)"]
                },
                Transitional: {
                    massing: "Clean vertical bays, simplified stone plinth.",
                    roof_type: "Angular Merlons",
                    window_ratio: "30% (Vertical grouping)",
                    materials: ["Cut Stone Cladding", "Textured Plaster (Earth tone)", "Aluminum-Wood"],
                    details: ["Simplified Triangular Bands", "Timber Awnings", "Rectangular recesses"]
                },
                Contemporary: {
                    massing: "Abstracted vertical volumes, distinct separation of base and body.",
                    roof_type: "Flush or Stepped Parapets",
                    window_ratio: "50% (Deeply recessed vertical slots)",
                    materials: ["Polished Red Granite", "Corten Steel", "Tinted Glass", "Textured Concrete"],
                    details: ["Fractal Geometric Patterns", "Hidden Lintels", "Dynamic Shadow Lines"]
                }
            },
            palette_instruction: "Respect the red-brick hue of the land. Primary: [RAL 060 80 10, RAL 060 70 40, RAL 2000]. Accents: [RAL 220 70 05, RAL 050 80 20].",
            pattern_images: [
                "/patterns/northern_najdi/nn_pattern_01.png",
                "/patterns/northern_najdi/nn_pattern_02.png",
                "/patterns/northern_najdi/nn_pattern_03.png"
            ],
            elements: [
                {
                    name: "Stepped Merlons",
                    type: "parapet",
                    description: "Distinctive stepped cresting on roof edges.",
                    prompt_terms: ["stepped merlons", "crenelated parapet", "najdi roof edge"]
                },
                {
                    name: "Triangular Frieze",
                    type: "decoration",
                    description: "Horizontal bands of triangular geometric relief",
                    prompt_terms: ["triangular frieze", "geometric relief band"]
                },
                {
                    name: "Turmah",
                    type: "window",
                    description: "Projecting wooden box lookout.",
                    prompt_terms: ["turmah projecting box", "wooden lookout"]
                }
            ]
        }
    },
    {
        id: 'hejazi_coast',
        name: 'Hejazi Coast',
        description: 'Red Sea architecture famous for its multi-story coral houses and elaborate projecting wooden roshans.',
        guideline_data: {
            // Placeholder: Not updated in recent migration
            default_style: 'Traditional',
            styles: {
                Traditional: { massing: 'Tall, multi-story', roof_type: 'Flat', window_ratio: 'High (via screens)', materials: ['Coral stone', 'Wood'] }
            },
            elements: [{ prompt_terms: ['Complex wooden Roshan', 'Projecting balconies', 'Carved wood screens'] }]
        }
    },
    {
        id: 'inner_madinah',
        name: 'Inner Madinah',
        description: 'Architecture of the holy city, characterized by black basalt stone and white plaster, with strong geometric patterns.',
        guideline_data: {
            // Placeholder: Not updated in recent migration
            default_style: 'Traditional',
            styles: {
                Traditional: { massing: 'Compact, geometric', roof_type: 'Flat', window_ratio: 'Medium', materials: ['Black basalt', 'White plaster'] }
            },
            elements: [{ prompt_terms: ['Black basalt texture', 'White geometric patterns', 'Arched entrances'] }]
        }
    },
    {
        id: 'abha_highlands',
        name: 'Abha Highlands',
        description: 'Mountain architecture adapted to rain and terrain, featuring stone towers, slate course protection (Raqf), and colorful frescoes.',
        guideline_data: {
            // Placeholder: Not updated in recent migration
            default_style: 'Traditional',
            styles: {
                Traditional: { massing: 'Tower-like, terraced', roof_type: 'Flat with slate overhangs', window_ratio: 'Small', materials: ['Stone', 'Slate (Raqf)', 'Mud'] }
            },
            elements: [{ prompt_terms: ['Raqf slate courses', 'Colorful frescoes (Al-Qatt Al-Asiri)', 'Stone tower house'] }]
        }
    }
];
